import { canAddControlPoint, canRemoveControlPoint, type EditorState } from '@rcs/editor';
import { ToolButton } from '@rcs/ui';
import { SEGMENT_STYLES } from '../course/segment-style';
import { messages } from '../i18n/ja';

/**
 * コースの制御点の一覧（位置と選択）。3D ビューでの選択と連動する。
 * 位置の数値入力はプロパティパネル（B-37）で行う。
 */
export function ControlPointList({
  state,
  onSelect,
  onAdd,
  onRemove,
}: {
  readonly state: EditorState;
  readonly onSelect: (id: string) => void;
  readonly onAdd: () => void;
  readonly onRemove: (id: string) => void;
}) {
  const t = messages.controlPoints;
  const selected = state.selectedId;
  return (
    <>
      <div className="control-points__actions" role="group" aria-label={t.actions}>
        <ToolButton
          label={t.add}
          disabled={!canAddControlPoint(state)}
          onClick={onAdd}
          data-testid="add-control-point"
          title={selected === undefined ? t.addAtEnd : t.addAfterSelected}
        />
        <ToolButton
          label={t.remove}
          disabled={selected === undefined || !canRemoveControlPoint(state, selected)}
          onClick={() => {
            if (selected !== undefined) onRemove(selected);
          }}
          data-testid="remove-control-point"
          aria-keyshortcuts="Delete"
        />
      </div>
      <ol className="control-points" aria-label={t.label} data-testid="control-points">
        {state.course.controlPoints.map((point, index) => {
          const selected = point.id === state.selectedId;
          const [x, y, z] = point.position;
          return (
            <li key={point.id}>
              <button
                type="button"
                className={
                  selected
                    ? 'control-points__item control-points__item--selected'
                    : 'control-points__item'
                }
                aria-pressed={selected}
                data-testid={`control-point-${point.id}`}
                data-position={`${x.toFixed(2)},${y.toFixed(2)},${z.toFixed(2)}`}
                onClick={() => {
                  onSelect(point.id);
                }}
              >
                <span className="control-points__index">{`${t.point} ${String(index + 1)}`}</span>
                <span className="control-points__position">
                  {`(${x.toFixed(1)}, ${y.toFixed(1)}, ${z.toFixed(1)}) m`}
                </span>
                {SEGMENT_STYLES[point.segment] !== undefined && (
                  <span
                    className="control-points__segment"
                    data-segment={point.segment}
                    style={{ borderColor: SEGMENT_STYLES[point.segment]?.css }}
                  >
                    {SEGMENT_STYLES[point.segment]?.icon}{' '}
                    {messages.properties.segments[point.segment]}
                  </span>
                )}
                {selected && <span className="control-points__badge">{t.selected}</span>}
              </button>
            </li>
          );
        })}
      </ol>
    </>
  );
}
